/* eslint-disable prettier/prettier */
import { Helper } from "./helper";

interface DetectKeyColumnsOptions {
  samplingThreshold?: number;
  samplingSize?: number;
  maxColumns?: number;
  batchSize?: number;
}

interface ColumnInfo {
  header: string;
  newIndex: number;
  oldIndex: number;
  letter: string;
  isExcluded: boolean;
  exclusionReason?: string;
}

export class KeyColumnDetector {
  private helper: Helper;

  constructor(state: any) {
    this.helper = new Helper(state);
  }

  private hasBlankValues(values: string[]): boolean {
    return values.some(value => !value || value.trim() === '');
  }

  private getAverageLength(values: string[]): number {
    if (values.length === 0) return 0;
    const totalLength = values.reduce((sum, value) => sum + value.length, 0);
    return totalLength / values.length;
  }

  private shouldExcludeColumn(values: string[]): { excluded: boolean; reason?: string } {
    if (this.hasBlankValues(values)) {
      return { excluded: true, reason: 'Contains blank values' };
    }

    if (values.some(value => this.helper.isDateValue(value))) {
      return { excluded: true, reason: 'Contains date values' };
    }

    const avgLength = this.getAverageLength(values);
    if (avgLength > 20) {
      return { excluded: true, reason: `Average length ${avgLength.toFixed(1)} > 20` };
    }

    return { excluded: false };
  }

  private generateCompositeKey(row: any[], columnIndices: number[]): string {
    return columnIndices
      .map(index => (row[index] !== undefined ? String(row[index]).toLowerCase() : ''))
      .join('|');
  }

  private generateCompositeKeyForDataset(row: any[], columnIndices: number[]): string {
    return columnIndices
      .map(index => (row[index] !== undefined ? String(row[index]).toLowerCase() : ''))
      .join('|');
  }

  private isFullyUnique(data: any[][], columnIndices: number[]): boolean {
    const keys = new Set<string>();

    for (let i = 1; i < data.length; i++) { // Skip header row
      const key = this.generateCompositeKey(data[i], columnIndices);
      if (keys.has(key)) {
        return false; // Found duplicate
      }
      keys.add(key);
    }

    return true;
  }

  private isFullyUniqueWithKeyGenerator(data: any[][], keyGenerator: (row: any[]) => string): boolean {
    const keys = new Set<string>();

    for (let i = 1; i < data.length; i++) { // Skip header row
      const key = keyGenerator(data[i]);
      if (keys.has(key)) {
        return false; // Found duplicate
      }
      keys.add(key);
    }

    return true;
  }

  private findUniqueCombination(
    newData: any[][],
    oldData: any[][],
    eligibleColumns: ColumnInfo[],
    maxColumns: number
  ): ColumnInfo[] | null {
    // Try combinations of 1 to maxColumns columns
    for (let size = 1; size <= maxColumns; size++) {
      const combinations = this.generateCombinations(eligibleColumns, size);

      for (const combination of combinations) {
        const newColumnIndices = combination.map(col => col.newIndex);
        const oldColumnIndices = combination.map(col => col.oldIndex);

        // Create key generators for each dataset
        const newKeyGenerator = (row: any[]) => this.generateCompositeKeyForDataset(row, newColumnIndices);
        const oldKeyGenerator = (row: any[]) => this.generateCompositeKeyForDataset(row, oldColumnIndices);

        // Check if this combination is 100% unique in both datasets
        const isNewUnique = this.isFullyUniqueWithKeyGenerator(newData, newKeyGenerator);
        const isOldUnique = this.isFullyUniqueWithKeyGenerator(oldData, oldKeyGenerator);

        if (isNewUnique && isOldUnique) {
          return combination;
        }
      }
    }

    return null;
  }

  private generateCombinations(columns: ColumnInfo[], size: number): ColumnInfo[][] {
    if (size === 0) return [[]];
    if (columns.length === 0) return [];

    const result: ColumnInfo[][] = [];

    for (let i = 0; i <= columns.length - size; i++) {
      const current = columns[i];
      const remaining = columns.slice(i + 1);
      const subCombinations = this.generateCombinations(remaining, size - 1);

      for (const subCombination of subCombinations) {
        result.push([current, ...subCombination]);
      }
    }

    return result;
  }

  private isColumnUnchanged(
    newData: any[][],
    oldData: any[][],
    newColumnIndex: number,
    oldColumnIndex: number
  ): boolean {
    // Get the minimum length to compare (in case datasets have different row counts)
    const minLength = Math.min(newData.length - 1, oldData.length - 1);

    if (minLength === 0) return true; // No data rows to compare

    // Compare each row value
    for (let i = 1; i <= minLength; i++) {
      const newValue = newData[i][newColumnIndex] !== undefined ? String(newData[i][newColumnIndex]) : "";
      const oldValue = oldData[i][oldColumnIndex] !== undefined ? String(oldData[i][oldColumnIndex]) : "";

      if (newValue !== oldValue) {
        return false; // Found a difference
      }
    }

    return true; // All values are identical
  }

  public async detectKeyColumns(
    newData: any[][],
    oldData: any[][],
    options: DetectKeyColumnsOptions = {}
  ) {
    const {
      samplingThreshold = 10000,
      samplingSize = 5000,
      maxColumns = 5,
      batchSize = 15,
    } = options;

    if (!newData.length || !oldData.length) {
      return { error: "Empty dataset provided" };
    }

    // Get headers from both datasets
    const newHeaders = newData[0];
    const oldHeaders = oldData[0];

    const newHeaderMap = new Map();
    const oldHeaderMap = new Map();

    // Create case-insensitive maps for header lookup
    newHeaders.forEach((header, index) => {
      if (header !== null && header !== undefined) {
        newHeaderMap.set(String(header).toLowerCase(), index);
      }
    });
    oldHeaders.forEach((header, index) => {
      if (header !== null && header !== undefined) {
        oldHeaderMap.set(String(header).toLowerCase(), index);
      }
    });

    // Find common headers between both datasets (case-insensitive)
    const commonHeaders = newHeaders.filter((header) => {
      if (header === null || header === undefined) return false;
      return oldHeaderMap.has(String(header).toLowerCase());
    });

    if (commonHeaders.length === 0) {
      return { error: "No common columns found between datasets" };
    }

    // Apply sampling if needed
    let newDataToProcess = newData;
    let oldDataToProcess = oldData;
    const useSampling = newData.length > samplingThreshold || oldData.length > samplingThreshold;

    if (useSampling) {
      newDataToProcess = [newData[0]];
      oldDataToProcess = [oldData[0]];

      const getRandomIndices = (max: number, count: number) => {
        const indices = new Set<number>();
        while (indices.size < count && indices.size < max) {
          indices.add(Math.floor(Math.random() * max) + 1);
        }
        return Array.from(indices);
      };

      const newIndices = getRandomIndices(newData.length - 1, samplingSize);
      const oldIndices = getRandomIndices(oldData.length - 1, samplingSize);

      newIndices.forEach((idx: number) => newDataToProcess.push(newData[idx]));
      oldIndices.forEach((idx: number) => oldDataToProcess.push(oldData[idx]));
    }

    // Analyze each common column for exclusion criteria
    const columnAnalysis: ColumnInfo[] = [];
    const excludedColumns: ColumnInfo[] = [];
    const unchangedColumns: ColumnInfo[] = [];

    for (const header of commonHeaders) {
      const headerLower = String(header).toLowerCase();
      const newColumnIndex = newHeaderMap.get(headerLower);
      const oldColumnIndex = oldHeaderMap.get(headerLower);

      // Extract column values (excluding headers)
      const newColumnValues = newDataToProcess
        .slice(1)
        .map((row) => (row[newColumnIndex] !== undefined ? String(row[newColumnIndex]) : ""));
      const oldColumnValues = oldDataToProcess
        .slice(1)
        .map((row) => (row[oldColumnIndex] !== undefined ? String(row[oldColumnIndex]) : ""));

      // Check if column is unchanged
      const isUnchanged = this.isColumnUnchanged(newDataToProcess, oldDataToProcess, newColumnIndex, oldColumnIndex);

      // Check exclusion criteria
      const newExclusion = this.shouldExcludeColumn(newColumnValues);
      const oldExclusion = this.shouldExcludeColumn(oldColumnValues);

      const columnInfo: ColumnInfo = {
        header,
        newIndex: newColumnIndex,
        oldIndex: oldColumnIndex,
        letter: this.helper.indexToColLetter(newColumnIndex),
        isExcluded: newExclusion.excluded || oldExclusion.excluded,
        exclusionReason: newExclusion.excluded ? newExclusion.reason : oldExclusion.reason,
      };

      if (columnInfo.isExcluded) {
        excludedColumns.push(columnInfo);
      } else {
        // Include both changed and unchanged columns for key detection
        columnAnalysis.push(columnInfo);
        // Track unchanged columns separately for reporting purposes
        if (isUnchanged) {
          unchangedColumns.push(columnInfo);
        }
      }
    }

    // Sort eligible columns by their original order (left to right)
    columnAnalysis.sort((a, b) => a.newIndex - b.newIndex);

    // Try to find unique combination using backtracking with batches
    let suggestedKeyColumns: ColumnInfo[] = [];

    for (let startIndex = 0; startIndex < columnAnalysis.length; startIndex += batchSize) {
      const batch = columnAnalysis.slice(startIndex, startIndex + batchSize);
      const combination = this.findUniqueCombination(newDataToProcess, oldDataToProcess, batch, maxColumns);

      if (combination) {
        suggestedKeyColumns = combination;
        break;
      }
    }

    // Prepare results
    const result = {
      suggestedKeyColumns: suggestedKeyColumns.map(col => col.header),
      suggestedKeyColumnLetters: suggestedKeyColumns.map(col => col.letter),
      suggestedIgnoreColumns: excludedColumns.map(col => col.header),
      unchangedColumns: unchangedColumns.map(col => col.header),
      unchangedColumnLetters: unchangedColumns.map(col => col.letter),
      commonHeaders,
      columnDetails: {
        eligible: columnAnalysis.map(col => ({
          header: col.header,
          letter: col.letter,
          index: col.newIndex,
        })),
        excluded: excludedColumns.map(col => ({
          header: col.header,
          letter: col.letter,
          index: col.newIndex,
          reason: col.exclusionReason,
        })),
        unchanged: unchangedColumns.map(col => ({
          header: col.header,
          letter: col.letter,
          index: col.newIndex,
        })),
      },
      samplingApplied: useSampling,
    };

    console.log("==========result==========");
    console.log(result);

    return result;
  }
}